import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import useFilters from 'src/registry/hooks/useFilters';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { PayStation } from 'src/features/payment/utils/types';
import {
    payStationCashLevelsFormSchema,
    PayStationCashLevelsFormSchemaValues,
} from 'src/features/payment/api/useChangeCoinBanknoteAvailability';
import { useListPayStationsCashLevels } from 'src/features/payment/api/useListPayStationCashLevels';
import { PayStationDeviceType } from 'src/features/payment/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import ChangeCashLevelsForm from 'src/features/payment/components/forms/ChangeCashLevelsForm';

interface ChangeCashLevelsFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation?: PayStation;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PayStationCashLevelsFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function ChangeCashLevelsFormModal<TData>({
    selectedPayStation,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: ChangeCashLevelsFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    const onError = useOnError();

    const { queryFiltersOptions } = useFilters(
        selectedPayStation?.id ? { initialQueryFilters: { pay_station: selectedPayStation?.id } } : undefined,
    );

    const { data: payStationCashLevels } = useListPayStationsCashLevels({
        parkingId,
        ...queryFiltersOptions,
    });

    const payStationBanknotes = React.useMemo(() => {
        return payStationCashLevels?.results
            .filter((p) => p.device_type === PayStationDeviceType.BANKNOTE_VALIDATOR)
            .filter((ps) => ps.in_recycle_box === false);
    }, [payStationCashLevels?.results]);

    const payStationCoins = React.useMemo(() => {
        return payStationCashLevels?.results
            .filter((p) => p.device_type === PayStationDeviceType.COIN_SELECTOR)
            .filter((ps) => ps.in_recycle_box === false);
    }, [payStationCashLevels?.results]);

    const defaultValues = React.useMemo(() => {
        return {
            coins: payStationCoins ? payStationCoins : [],
            banknotes: payStationBanknotes ? payStationBanknotes : [],
        };
    }, [payStationCoins, payStationBanknotes]);

    const form = useForm<PayStationCashLevelsFormSchemaValues>({
        resolver: zodResolver(payStationCashLevelsFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PayStationCashLevelsFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ChangeCashLevelsForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedPayStation && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ChangeCashLevelsFormModal;
